<script setup lang="ts">
  import {
    type SuperUserOrganisation as Organisation,
    type PatchedSuperUserOrganisationRequest as PatchedOrganisationRequest,
    type StripeCustomersListResponse,
    type SuperUserStripeCustomer as StripeCustomer,
    type LawFields,
    type SuperUserHubSpotCompany as HubSpotCompany,
    type SuperUserOrganisation
  } from '@/types/generated/types.gen'
  import {
    OrganisationsService,
    StripeCustomersService,
    LawFieldsService,
    HubspotService
  } from '@/types/generated/services.gen'
  import { OpenAPI, CancelablePromise } from '@/types/generated/'
  import { request as __request } from '@/types/generated/core/request'
  import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import router from '@/router'
  import { type GenericObject } from 'vee-validate'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import { useTranslation } from '@/composables/useTranslation'
  import { $SuperUserOrganisation as $Organisation } from '@/types/generated'
  import FormGrid from '@/components/admin/form/FormGrid.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import { toast } from '@/components/ui/toast'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'

  // Type for HubSpot company candidates from the find-for-organisation endpoint
  interface HubSpotCompanyCandidate {
    uuid: string
    hubspot_id: string
    name: string | null
    domain: string | null
    hubspot_url: string
    matched_domains: string[]
  }

  // Helper function to call the find-for-organisation endpoint
  function findHubSpotCompaniesForOrganisation(
    organisationUuid: string
  ): CancelablePromise<HubSpotCompanyCandidate[]> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v3/hubspot/companies/find-for-organisation/',
      query: {
        organisation_uuid: organisationUuid
      }
    })
  }

  const { t } = useTranslation({
    en: {
      general: 'General',
      features: 'Features',
      botSettings: 'Bot Settings',
      viewBotSettings: 'Chatbot',

      name: 'Lawirm Name',
      street: 'Street',
      house: 'House',
      postcode: 'Postcode',
      city: 'City',
      country: 'Country',
      email: 'Email',
      phone: 'Phone',
      seats: 'Seats',
      appointmentBookingSeats: 'Appointment Booking Seats',
      connections: 'Connections',
      stripeConnection: 'Stripe Connection',
      lawFields: 'Law Fields',
      jupusPlatform: 'JUPUS Platform',
      jupusPhoneAI: 'JUPUS Phone AI',
      appointments: 'Appointments',
      documentAI: 'Document AI',
      aiWorkflows: 'AI Workflows',
      aiEnabled: 'AI Enabled',
      jupusPlatformSettings: 'JUPUS Platform Settings',
      monsumCustomerNumber: 'Monsum Customer Number',
      isTest: 'Test Organization',
      inboundEmailSlug: 'JUPUS Email',
      enableInboundEmailReplies: 'Enable Email Replies from Clients in JUPUS',
      canConfigureEmail: 'Custom Email Server',
      isNewUiSwitchEnabled: 'Enable New UI for Testing (can switch freely)',
      isForcedToUseNewUi: 'Enable New UI by Default (cannot switch)',
      uiSettings: 'UI Settings',
      onboardingStartDate: 'Onboarding Start Date',
      onboardingTime: 'Onboarding Time',
      hubspotConnection: 'HubSpot Connection',
      hasChurned: 'Has Churned',
      activeCustomer: 'Active Customer',
      churnedCustomer: 'Churned Customer',
      jupusPhonebotSettings: 'JUPUS Phonebot Settings',
      isPhonebotSelfServiceCustomer: 'Is Phonebot Self-Service Customer',
      phonebotSelfServicePlan: 'Phonebot Self-Service Plan',
      phonebotEnterpriseIncludedMinutes: 'Enterprise Included Minutes',
      // HubSpot modal translations
      findHubspotMatch: 'Find HubSpot Company',
      hubspotNotConnected: 'No HubSpot company connected',
      hubspotNotConnectedDescription: 'Click below to search for and connect a HubSpot company.',
      connectHubspot: 'Connect HubSpot Company',
      removeConnection: 'Remove',
      hubspotModalTitle: 'Connect HubSpot Company',
      suggestedMatches: 'Suggested Matches',
      suggestedMatchesDescription: 'Based on email domains from this organisation',
      allCompanies: 'All HubSpot Companies',
      searchPlaceholder: 'Search by name or domain...',
      selectCompany: 'Select',
      noSuggestionsFound: 'No automatic matches found',
      noResultsFound: 'No companies found',
      searching: 'Searching...',
      close: 'Close',
      openInHubSpot: 'Open in HubSpot',
      change: 'Change',
      unnamedCompany: 'Unnamed Company',
      unnamed: 'Unnamed',
      orSearchAllCompanies: 'or search all companies',
      typeToSearch: 'Type to search for HubSpot companies...',
      matched: 'Matched',
      // Error messages
      error: 'Error',
      hubspotConnectError: 'Failed to connect HubSpot company. Please try again.',
      hubspotRemoveError: 'Failed to remove HubSpot connection. Please try again.',
      churnedStatusError: 'Failed to update customer status. Please try again.',
      customerNumber: 'Customer Number',
      // Onboarding date/time validation
      onboardingInvalidDateFormat: 'Invalid date format',
      onboardingInvalidDateFormatDescription: 'Date must be in DD.MM.YYYY format',
      onboardingInvalidDate: 'Invalid date',
      onboardingInvalidDateDescription: 'Date components must be valid numbers',
      onboardingInvalidTimeFormat: 'Invalid time format',
      onboardingInvalidTimeFormatDescription: 'Time must be in HH:MM format',
      onboardingInvalidTime: 'Invalid time',
      onboardingInvalidTimeDescription: 'Time components must be valid numbers',
      onboardingInvalidDateTime: 'Invalid date/time',
      onboardingInvalidDateTimeDescription: 'The provided date and time combination is not valid',
      // Churn confirmation dialog
      churnConfirmTitle: 'Confirm Customer Status Change',
      churnConfirmDescription:
        'Are you sure you want to mark this customer as churned? This may trigger data cleanup processes.',
      churnConfirmAction: 'Yes, mark as churned',
      cancel: 'Cancel'
    },
    de: {
      general: 'Allgemein',
      features: 'Features',
      botSettings: 'Bot Einstellungen',
      viewBotSettings: 'Chatbot',
      name: 'Name der Kanzlei',
      street: 'Straße',
      house: 'Haus',
      postcode: 'PLZ',
      city: 'Stadt',
      country: 'Land',
      email: 'Email',
      phone: 'Telefon',
      seats: 'Seats',
      appointmentBookingSeats: 'Terminbuchung Seats',
      connections: 'Verbindungen',
      stripeConnection: 'Stripe Verbindung',
      lawFields: 'Rechtsgebiete',
      jupusPlatform: 'JUPUS Plattform',
      jupusPhoneAI: 'JUPUS Phone AI',
      appointments: 'Terminbuchung',
      documentAI: 'Dokumenten-KI',
      aiWorkflows: 'KI-Workflows',
      aiEnabled: 'KI aktivieren',
      jupusPlatformSettings: 'JUPUS Plattform Einstellungen',
      monsumCustomerNumber: 'Monsum Kundennummer',
      isTest: 'Test-Organisation',
      inboundEmailSlug: 'JUPUS Email',
      enableInboundEmailReplies: 'E-Mail Antwort von Mandanten in JUPUS',
      canConfigureEmail: 'Eigener E-Mail-Server',
      isNewUiSwitchEnabled: 'Neues UI zum Ausprobieren aktivieren (kann beliebig wechseln)',
      isForcedToUseNewUi: 'Neues UI standardmäßig aktivieren (kann nicht wechseln)',
      uiSettings: 'UI Einstellungen',
      onboardingStartDate: 'Onboarding Startdatum',
      onboardingTime: 'Onboarding Uhrzeit',
      hubspotConnection: 'HubSpot Verbindung',
      hasChurned: 'Hat gekündigt',
      activeCustomer: 'Aktiver Kunde',
      churnedCustomer: 'Gekündigter Kunde',
      jupusPhonebotSettings: 'JUPUS Phonebot Einstellungen',
      isPhonebotSelfServiceCustomer: 'Ist Phonebot Self-Service Kunde',
      phonebotSelfServicePlan: 'Phonebot Self-Service Plan',
      phonebotEnterpriseIncludedMinutes: 'Enterprise Inklusivminuten',
      // HubSpot modal translations
      findHubspotMatch: 'HubSpot-Unternehmen finden',
      hubspotNotConnected: 'Kein HubSpot-Unternehmen verbunden',
      hubspotNotConnectedDescription:
        'Klicken Sie unten, um ein HubSpot-Unternehmen zu suchen und zu verbinden.',
      connectHubspot: 'HubSpot-Unternehmen verbinden',
      removeConnection: 'Entfernen',
      hubspotModalTitle: 'HubSpot-Unternehmen verbinden',
      suggestedMatches: 'Vorgeschlagene Übereinstimmungen',
      suggestedMatchesDescription: 'Basierend auf E-Mail-Domains dieser Organisation',
      allCompanies: 'Alle HubSpot-Unternehmen',
      searchPlaceholder: 'Nach Name oder Domain suchen...',
      selectCompany: 'Auswählen',
      noSuggestionsFound: 'Keine automatischen Übereinstimmungen gefunden',
      noResultsFound: 'Keine Unternehmen gefunden',
      searching: 'Suche läuft...',
      close: 'Schließen',
      openInHubSpot: 'In HubSpot öffnen',
      change: 'Ändern',
      unnamedCompany: 'Unbenanntes Unternehmen',
      unnamed: 'Unbenannt',
      orSearchAllCompanies: 'oder alle Unternehmen durchsuchen',
      typeToSearch: 'Zum Suchen von HubSpot-Unternehmen tippen...',
      matched: 'Übereinstimmung',
      // Error messages
      error: 'Fehler',
      hubspotConnectError:
        'HubSpot-Unternehmen konnte nicht verbunden werden. Bitte versuchen Sie es erneut.',
      hubspotRemoveError:
        'HubSpot-Verbindung konnte nicht entfernt werden. Bitte versuchen Sie es erneut.',
      churnedStatusError:
        'Kundenstatus konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
      // Onboarding date/time validation
      onboardingInvalidDateFormat: 'Ungültiges Datumsformat',
      onboardingInvalidDateFormatDescription: 'Datum muss im Format TT.MM.JJJJ sein',
      onboardingInvalidDate: 'Ungültiges Datum',
      onboardingInvalidDateDescription: 'Datumskomponenten müssen gültige Zahlen sein',
      onboardingInvalidTimeFormat: 'Ungültiges Zeitformat',
      onboardingInvalidTimeFormatDescription: 'Zeit muss im Format HH:MM sein',
      onboardingInvalidTime: 'Ungültige Zeit',
      onboardingInvalidTimeDescription: 'Zeitkomponenten müssen gültige Zahlen sein',
      onboardingInvalidDateTime: 'Ungültiges Datum/Uhrzeit',
      onboardingInvalidDateTimeDescription:
        'Die angegebene Datums- und Zeitkombination ist nicht gültig',
      customerNumber: 'Kundennummer',
      // Churn confirmation dialog
      churnConfirmTitle: 'Kundenstatusänderung bestätigen',
      churnConfirmDescription:
        'Sind Sie sicher, dass Sie diesen Kunden als gekündigt markieren möchten? Dies kann Datenbereinigungsprozesse auslösen.',
      churnConfirmAction: 'Ja, als gekündigt markieren',
      cancel: 'Abbrechen'
    }
  })

  const org = ref<SuperUserOrganisation | undefined>(undefined)
  const stripeCustomersList = ref<StripeCustomersListResponse>()
  const stripeCustomersOptions = computed(() => {
    return (
      stripeCustomersList.value?.results.map((customer) => ({
        value: customer.uuid,
        title: customer.name?.toString() + ' - ' + customer.email?.toString()
      })) ?? []
    )
  })
  const stripeCustomer = computed<StripeCustomer>(() => {
    return (
      stripeCustomersList.value?.results.find(
        (customer) => customer.uuid === org.value?.stripe_customer_uuid
      ) ?? {}
    )
  })

  const lawFields = ref<LawFields>()
  const lawFieldOptions = computed(() => {
    return (
      Object.keys(lawFields.value?.data ?? {}).map((key) => ({
        value: key,
        title: key
      })) ?? []
    )
  })

  // Churn confirmation dialog state
  const showChurnConfirmDialog = ref(false)

  // HubSpot modal state
  const showHubspotModal = ref(false)
  const hubspotCandidates = ref<HubSpotCompanyCandidate[]>([])
  const hubspotSearchResults = ref<HubSpotCompany[]>([])
  const hubspotSearchQuery = ref('')
  const isSearchingCandidates = ref(false)
  const isSearchingAll = ref(false)
  const isSelectingHubspotCompany = ref<string | null>(null) // tracks uuid of company being selected

  const openHubspotModal = async () => {
    showHubspotModal.value = true
    hubspotSearchQuery.value = ''
    hubspotSearchResults.value = []

    // Automatically search for candidates when opening modal
    if (org.value) {
      isSearchingCandidates.value = true
      try {
        hubspotCandidates.value = await findHubSpotCompaniesForOrganisation(org.value.uuid)
      } finally {
        isSearchingCandidates.value = false
      }
    }
  }

  const closeHubspotModal = () => {
    showHubspotModal.value = false
    hubspotCandidates.value = []
    hubspotSearchResults.value = []
    hubspotSearchQuery.value = ''
    isSelectingHubspotCompany.value = null
  }

  const searchHubspotCompanies = async () => {
    if (!hubspotSearchQuery.value.trim()) {
      hubspotSearchResults.value = []
      return
    }

    isSearchingAll.value = true
    try {
      const response = await HubspotService.hubspotCompaniesList({
        search: hubspotSearchQuery.value,
        limit: 20
      })
      hubspotSearchResults.value = response.results
    } finally {
      isSearchingAll.value = false
    }
  }

  // Debounced search
  let searchTimeout: ReturnType<typeof setTimeout> | null = null
  watch(hubspotSearchQuery, () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      searchHubspotCompanies()
    }, 300)
  })

  // Cleanup timeout on component unmount to prevent memory leaks
  onBeforeUnmount(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
      searchTimeout = null
    }
  })

  const selectHubspotCompany = async (company: { uuid: string }) => {
    if (!org.value) return

    isSelectingHubspotCompany.value = company.uuid
    try {
      await handleSave({ hubspot_company_uuid: company.uuid })
      closeHubspotModal()
    } catch (error) {
      toast({
        title: t('error'),
        description: t('hubspotConnectError'),
        variant: 'destructive'
      })
    } finally {
      isSelectingHubspotCompany.value = null
    }
  }

  const removeHubspotConnection = async () => {
    if (!org.value) return

    try {
      await handleSave({ hubspot_company_uuid: null })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('hubspotRemoveError'),
        variant: 'destructive'
      })
    }
  }

  const toggleChurnedStatus = async () => {
    if (!org.value) return

    // If marking as churned (currently active), show confirmation dialog
    if (!org.value.has_churned) {
      showChurnConfirmDialog.value = true
      return
    }

    // If marking as active (currently churned), proceed directly
    await performChurnStatusChange()
  }

  const confirmChurnStatus = async () => {
    showChurnConfirmDialog.value = false
    await performChurnStatusChange()
  }

  const cancelChurnConfirm = () => {
    showChurnConfirmDialog.value = false
  }

  const performChurnStatusChange = async () => {
    if (!org.value) return

    const previousValue = org.value.has_churned
    org.value.has_churned = !org.value.has_churned

    try {
      await handleSave({ has_churned: org.value.has_churned })
    } catch (error) {
      // Rollback on failure
      org.value.has_churned = previousValue
      toast({
        title: t('error'),
        description: t('churnedStatusError'),
        variant: 'destructive'
      })
    }
  }

  // Onboarding date/time management
  const onboardingDate = ref<string | null>(null)
  const onboardingTime = ref<string>('09:30')

  // Initialize onboarding date/time from org
  watch(
    () => org.value?.onboarding_start_date,
    (newDate) => {
      if (newDate) {
        const date = new Date(newDate)
        // Format as DD.MM.YYYY
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        onboardingDate.value = `${day}.${month}.${year}`
        // Format time as HH:MM
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        onboardingTime.value = `${hours}:${minutes}`
      } else {
        onboardingDate.value = null
        onboardingTime.value = '09:30'
      }
    },
    { immediate: true }
  )

  const saveOnboardingDateTime = async (value) => {
    if (!org.value) return

    // If no date value is provided, clear the onboarding_start_date
    if (!value) {
      await handleSave({ onboarding_start_date: null })
      return
    }

    // Validate and parse date components
    const dateParts = value.split('.')
    if (dateParts.length !== 3) {
      toast({
        title: t('onboardingInvalidDateFormat'),
        description: t('onboardingInvalidDateFormatDescription'),
        variant: 'destructive'
      })
      return
    }

    const [day, month, year] = dateParts
    const dayNum = Number(day)
    const monthNum = Number(month)
    const yearNum = Number(year)

    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
      toast({
        title: t('onboardingInvalidDate'),
        description: t('onboardingInvalidDateDescription'),
        variant: 'destructive'
      })
      return
    }

    // Validate and parse time components
    const timeParts = onboardingTime.value.split(':')
    if (timeParts.length !== 2) {
      toast({
        title: t('onboardingInvalidTimeFormat'),
        description: t('onboardingInvalidTimeFormatDescription'),
        variant: 'destructive'
      })
      return
    }

    const [hours, minutes] = timeParts
    const hoursNum = Number(hours)
    const minutesNum = Number(minutes)

    if (isNaN(hoursNum) || isNaN(minutesNum)) {
      toast({
        title: t('onboardingInvalidTime'),
        description: t('onboardingInvalidTimeDescription'),
        variant: 'destructive'
      })
      return
    }

    // Create date and validate it's a valid date
    const date = new Date(yearNum, monthNum - 1, dayNum, hoursNum, minutesNum)

    if (isNaN(date.getTime())) {
      toast({
        title: t('onboardingInvalidDateTime'),
        description: t('onboardingInvalidDateTimeDescription'),
        variant: 'destructive'
      })
      return
    }

    // Save the valid date
    await handleSave({ onboarding_start_date: date.toISOString() })
  }

  onMounted(async () => {
    org.value = (await OrganisationsService.organisationsRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as Organisation
    stripeCustomersList.value = await StripeCustomersService.stripeCustomersList({ limit: 0 })
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  })

  // Function to reload the organization data
  const reloadOrganization = async () => {
    if (org.value) {
      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: org.value.uuid
      })) as Organisation
    }
  }

  const handleSave = async (values: GenericObject) => {
    await OrganisationsService.organisationsPartialUpdate({
      uuid: org.value!.uuid,
      requestBody: values as PatchedOrganisationRequest
    })

    // Reload the organization data after save to get the updated information
    await reloadOrganization()
  }
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <template v-if="org">
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST },
            {
              title: `Edit ${org.uuid}`,
              to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST.replace(':uuid', org.uuid)
            }
          ]"
        />

        <div class="flex items-center gap-3">
          <!-- Bot Settings Button -->
          <router-link
            :to="ROUTES.ADMIN.CUSTOMERS.CUSTOMER_BOT_SETTINGS.replace(':uuid', org.uuid)"
            class="inline-flex items-center gap-2 rounded-full bg-purple-100 px-4 py-2 text-sm font-medium
              text-purple-700 transition-all duration-200 hover:bg-purple-200 dark:bg-purple-900/30
              dark:text-purple-400 dark:hover:bg-purple-900/50"
          >
            <svg
              class="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {{ t('viewBotSettings') }}
          </router-link>

          <!-- Customer Status Toggle -->
          <button
            type="button"
            class="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200"
            :class="
              org.has_churned
                ? `bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400
                  dark:hover:bg-red-900/50`
                : `bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400
                  dark:hover:bg-green-900/50`
            "
            @click="toggleChurnedStatus"
          >
            <span
              class="h-2.5 w-2.5 rounded-full"
              :class="org.has_churned ? 'bg-red-500' : 'bg-green-500'"
            />
            {{ org.has_churned ? t('churnedCustomer') : t('activeCustomer') }}
            <svg
              class="h-4 w-4 opacity-60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 9l4-4 4 4m0 6l-4 4-4-4"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <AutoSavingForm @save="handleSave">
          <div class="grid grid-cols-3 gap-8">
            <div class="col-span-2">
              <div class="mb-4 font-semibold">
                {{ t('general') }}
              </div>
              <hr class="mb-4" />
              <InputField
                v-model="org.name"
                :label="t('name')"
                name="name"
                :rules="yup.string().required()"
              />
              <FormGrid>
                <div class="col-span-9">
                  <InputField
                    v-model="org.street"
                    :label="t('street')"
                    name="street"
                    :rules="yup.string().required()"
                  />
                </div>
                <div class="col-span-3">
                  <InputField
                    v-model="org.street_number"
                    :label="t('house')"
                    name="street_number"
                    :rules="yup.string().max($Organisation.properties.street.maxLength).required()"
                  />
                </div>
              </FormGrid>
              <FormGrid>
                <div class="col-span-3">
                  <InputField
                    v-model="org.zip"
                    :label="t('postcode')"
                    name="zip"
                    :rules="yup.string().max($Organisation.properties.zip.maxLength).required()"
                  />
                </div>
                <div class="col-span-9">
                  <InputField
                    v-model="org.city"
                    :label="t('city')"
                    name="city"
                    :rules="yup.string().max($Organisation.properties.city.maxLength).required()"
                  />
                </div>
              </FormGrid>
              <InputField
                v-model="org.country"
                :label="t('country')"
                name="country"
                :rules="yup.string().max($Organisation.properties.country.maxLength).required()"
              />
              <InputField
                v-model="org.email"
                :label="t('email')"
                name="email"
                :rules="yup.string().email().required()"
              />
              <InputField
                v-model="org.phone"
                :label="t('phone')"
                name="phone"
                :rules="yup.string().max($Organisation.properties.phone.maxLength).required()"
              />
              <FormGrid>
                <div class="col-span-6">
                  <InputField
                    v-model="org.customer_number"
                    :label="t('customerNumber')"
                    name="customer_number"
                    :disabled="true"
                  />
                </div>
                <div class="col-span-6">
                  <InputField
                    v-model="org.monsum_customer_number"
                    :label="t('monsumCustomerNumber')"
                    name="monsum_customer_number"
                    :rules="yup.string().notRequired()"
                  />
                </div>
              </FormGrid>
              <InputField
                v-model="org.inbound_email_address"
                :label="t('inboundEmailSlug')"
                name="inbound_email_address"
                :disabled="true"
              />
              <CheckboxField
                v-model="org.is_test"
                :label="t('isTest')"
                name="is_test"
              />
            </div>
            <div class="col-span-1">
              <div class="pb-3 text-base font-semibold text-gray-800 sm:text-lg">
                {{ t('connections') }}
              </div>
              <hr class="mb-4" />
              <SelectField
                v-model="org.stripe_customer_uuid"
                name="stripe_customer_uuid"
                :label="t('stripeConnection')"
                :is-filterable="true"
                :options="stripeCustomersOptions"
                :rules="yup.string().uuid().notRequired()"
              />
              <a
                v-if="stripeCustomer"
                target="_blank"
                class="text-xs text-purple-600"
                :href="stripeCustomer.stripe_url"
              >
                {{ stripeCustomer.stripe_url }}
              </a>
              <!-- HubSpot Connection Section -->
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('hubspotConnection') }}
                </label>

                <!-- Connected State: Show company card -->
                <div
                  v-if="org.hubspot_company"
                  class="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/50 dark:bg-orange-950/30"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <!-- HubSpot icon -->
                        <svg
                          class="h-5 w-5 shrink-0 text-orange-600"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M17.69 14.57c-.43 0-.82.11-1.18.32l-1.95-1.47c.15-.35.24-.74.24-1.15 0-.52-.13-1.01-.36-1.44l2.01-2.01c.26.1.53.16.82.16 1.24 0 2.25-1.01 2.25-2.25S18.51 4.48 17.27 4.48 15.02 5.49 15.02 6.73c0 .29.06.57.16.82l-2.01 2.01c-.43-.23-.92-.36-1.44-.36-.78 0-1.49.27-2.05.72L6.61 7.25c.03-.12.05-.25.05-.38 0-.9-.73-1.62-1.62-1.62-.9 0-1.62.73-1.62 1.62s.73 1.62 1.62 1.62c.38 0 .72-.14 1-.36l3.06 2.67c-.23.43-.36.92-.36 1.44 0 1.66 1.27 3.02 2.89 3.17v2.45c-.65.17-1.13.76-1.13 1.46 0 .84.68 1.52 1.52 1.52s1.52-.68 1.52-1.52c0-.7-.48-1.29-1.13-1.46v-2.45c1.01-.1 1.88-.64 2.43-1.42l1.95 1.47c-.08.23-.12.47-.12.73 0 1.24 1.01 2.25 2.25 2.25s2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25z"
                          />
                        </svg>
                        <span class="font-medium text-gray-900 dark:text-gray-100">
                          {{ org.hubspot_company.name || t('unnamedCompany') }}
                        </span>
                      </div>
                      <div
                        v-if="org.hubspot_company.domain"
                        class="mt-1 text-sm text-gray-600 dark:text-gray-400"
                      >
                        {{ org.hubspot_company.domain }}
                      </div>
                      <a
                        :href="org.hubspot_company.hubspot_url"
                        target="_blank"
                        class="mt-1 inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 hover:underline
                          dark:text-orange-400"
                      >
                        <span>{{ t('openInHubSpot') }}</span>
                        <svg
                          class="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                    <div class="flex shrink-0 gap-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-orange-700 transition-colors hover:bg-orange-50 focus:outline-none focus:ring-2
                          focus:ring-orange-500 focus:ring-offset-1 dark:border-orange-700 dark:bg-orange-950
                          dark:text-orange-300 dark:hover:bg-orange-900"
                        @click="openHubspotModal"
                      >
                        {{ t('change') }}
                      </button>
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-red-700 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2
                          focus:ring-red-500 focus:ring-offset-1 dark:border-red-700 dark:bg-red-950 dark:text-red-300
                          dark:hover:bg-red-900"
                        @click="removeHubspotConnection"
                      >
                        {{ t('removeConnection') }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Not Connected State: Show connect button -->
                <div
                  v-else
                  class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-neutral-600
                    dark:bg-neutral-800"
                >
                  <svg
                    class="mx-auto h-8 w-8 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M17.69 14.57c-.43 0-.82.11-1.18.32l-1.95-1.47c.15-.35.24-.74.24-1.15 0-.52-.13-1.01-.36-1.44l2.01-2.01c.26.1.53.16.82.16 1.24 0 2.25-1.01 2.25-2.25S18.51 4.48 17.27 4.48 15.02 5.49 15.02 6.73c0 .29.06.57.16.82l-2.01 2.01c-.43-.23-.92-.36-1.44-.36-.78 0-1.49.27-2.05.72L6.61 7.25c.03-.12.05-.25.05-.38 0-.9-.73-1.62-1.62-1.62-.9 0-1.62.73-1.62 1.62s.73 1.62 1.62 1.62c.38 0 .72-.14 1-.36l3.06 2.67c-.23.43-.36.92-.36 1.44 0 1.66 1.27 3.02 2.89 3.17v2.45c-.65.17-1.13.76-1.13 1.46 0 .84.68 1.52 1.52 1.52s1.52-.68 1.52-1.52c0-.7-.48-1.29-1.13-1.46v-2.45c1.01-.1 1.88-.64 2.43-1.42l1.95 1.47c-.08.23-.12.47-.12.73 0 1.24 1.01 2.25 2.25 2.25s2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25z"
                    />
                  </svg>
                  <p class="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    {{ t('hubspotNotConnected') }}
                  </p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {{ t('hubspotNotConnectedDescription') }}
                  </p>
                  <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-2 rounded-md bg-orange-600 px-3 py-2 text-sm font-medium
                      text-white transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2
                      focus:ring-orange-500 focus:ring-offset-2"
                    @click="openHubspotModal"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {{ t('connectHubspot') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Onboarding Date/Time Widget -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ t('onboardingStartDate') }}
            </h3>
            <FormGrid>
              <div class="col-span-8">
                <DatePickerField
                  v-model="onboardingDate"
                  :label="t('onboardingStartDate')"
                  name="onboarding_start_date"
                  @update:model-value="saveOnboardingDateTime"
                />
              </div>
              <div class="col-span-4">
                <label class="mb-2 block text-sm font-medium">{{ t('onboardingTime') }}</label>
                <input
                  v-model="onboardingTime"
                  type="time"
                  class="block w-full cursor-text rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:z-10
                    focus:border-purple-600 focus:ring-purple-600"
                  @change="saveOnboardingDateTime(onboardingDate)"
                />
              </div>
            </FormGrid>
          </div>

          <!-- JUPUS Phonebot Settings Widget -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ t('jupusPhonebotSettings') }}
            </h3>
            <CheckboxField
              v-model="org.is_phonebot_self_service_customer"
              :label="t('isPhonebotSelfServiceCustomer')"
              name="is_phonebot_self_service_customer"
            />
            <SelectField
              v-model="org.phonebot_self_service_plan"
              name="phonebot_self_service_plan"
              :label="t('phonebotSelfServicePlan')"
              :options="[
                { value: '', title: '-' },
                { value: 'phone_beginner_plan', title: 'Phone Beginner' },
                { value: 'phone_professional_plan', title: 'Phone Professional' },
                { value: 'phone_professional_plus_plan', title: 'Phone Professional Plus' },
                { value: 'phone_enterprise_plan', title: 'Phone Enterprise' }
              ]"
            />
            <InputField
              v-model="org.phonebot_self_service_plan_enterprise_included_minutes"
              type="number"
              :label="t('phonebotEnterpriseIncludedMinutes')"
              name="phonebot_self_service_plan_enterprise_included_minutes"
              :rules="yup.number().min(0)"
            />
          </div>

          <!-- Law Fields Widget -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ t('lawFields') }}
            </h3>
            <MultiSelectField
              v-model="org.law_fields"
              :options="lawFieldOptions"
              name="law_fields"
            />
          </div>

          <!-- JUPUS Platform Settings Card -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <div class="mb-4 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {{ t('jupusPlatformSettings') }}
              </h3>
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </div>

            <!-- Seats Row -->
            <FormGrid>
              <div class="col-span-6">
                <InputField
                  v-model="org.seats"
                  type="number"
                  :label="t('seats')"
                  name="seats"
                  :rules="yup.number().required()"
                />
              </div>
              <div class="col-span-6">
                <InputField
                  v-model="org.appointments_seats"
                  type="number"
                  :label="t('appointmentBookingSeats')"
                  name="appointments_seats"
                  :rules="yup.number().required()"
                />
              </div>
            </FormGrid>

            <!-- Features Section -->
            <div
              class="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-neutral-700 dark:bg-neutral-900"
            >
              <h4 class="mb-4 font-semibold text-gray-900 dark:text-gray-100">
                {{ t('features') }}
              </h4>
              <div class="space-y-3">
                <CheckboxField
                  v-model="org.enable_cases"
                  :label="t('jupusPlatform')"
                  name="enable_cases"
                />
                <CheckboxField
                  v-model="org.enable_voice"
                  :label="t('jupusPhoneAI')"
                  name="enable_voice"
                />
                <CheckboxField
                  v-model="org.enable_appointments"
                  :label="t('appointments')"
                  name="enable_appointments"
                />
                <CheckboxField
                  v-model="org.enable_law_specific_data"
                  :label="t('documentAI')"
                  name="enable_law_specific_data"
                />
                <CheckboxField
                  v-model="org.ai_enabled"
                  :label="t('aiEnabled')"
                  name="ai_enabled"
                />
                <CheckboxField
                  v-model="org.enable_ai_workflows"
                  :label="t('aiWorkflows')"
                  name="enable_ai_workflows"
                />
              </div>
            </div>
          </div>

          <!-- UI Settings Card -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <div class="mb-4 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {{ t('uiSettings') }}
              </h3>
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
            </div>
            <div class="space-y-3">
              <CheckboxField
                v-model="org.is_new_ui_switch_enabled"
                :label="t('isNewUiSwitchEnabled')"
                name="is_new_ui_switch_enabled"
              />
              <CheckboxField
                v-model="org.is_forced_to_use_new_ui"
                :label="t('isForcedToUseNewUi')"
                name="is_forced_to_use_new_ui"
              />
            </div>
          </div>

          <!-- Email Settings Card -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <div class="mb-4 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {{ t('inboundEmailSlug') }}
              </h3>
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <InputField
              v-model="org.inbound_email_slug"
              :label="t('inboundEmailSlug')"
              name="inbound_email_slug"
              :rules="yup.string().notRequired()"
            />
            <div class="space-y-3">
              <CheckboxField
                v-model="org.enable_inbound_email_replies"
                :label="t('enableInboundEmailReplies')"
                name="enable_inbound_email_replies"
              />
              <CheckboxField
                v-model="org.can_configure_email"
                :label="t('canConfigureEmail')"
                name="can_configure_email"
              />
            </div>
          </div>
        </AutoSavingForm>
      </div>
    </template>
    <!-- HubSpot Company Selection Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showHubspotModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          @click.self="closeHubspotModal"
        >
          <Transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="duration-150 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <div
              v-if="showHubspotModal"
              data-cy="hubspot-modal"
              class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl
                dark:bg-neutral-800"
            >
              <!-- Modal Header -->
              <div
                class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <div class="flex items-center gap-3">
                  <div class="rounded-lg bg-orange-100 p-2 dark:bg-orange-900/30">
                    <svg
                      class="h-5 w-5 text-orange-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M17.69 14.57c-.43 0-.82.11-1.18.32l-1.95-1.47c.15-.35.24-.74.24-1.15 0-.52-.13-1.01-.36-1.44l2.01-2.01c.26.1.53.16.82.16 1.24 0 2.25-1.01 2.25-2.25S18.51 4.48 17.27 4.48 15.02 5.49 15.02 6.73c0 .29.06.57.16.82l-2.01 2.01c-.43-.23-.92-.36-1.44-.36-.78 0-1.49.27-2.05.72L6.61 7.25c.03-.12.05-.25.05-.38 0-.9-.73-1.62-1.62-1.62-.9 0-1.62.73-1.62 1.62s.73 1.62 1.62 1.62c.38 0 .72-.14 1-.36l3.06 2.67c-.23.43-.36.92-.36 1.44 0 1.66 1.27 3.02 2.89 3.17v2.45c-.65.17-1.13.76-1.13 1.46 0 .84.68 1.52 1.52 1.52s1.52-.68 1.52-1.52c0-.7-.48-1.29-1.13-1.46v-2.45c1.01-.1 1.88-.64 2.43-1.42l1.95 1.47c-.08.23-.12.47-.12.73 0 1.24 1.01 2.25 2.25 2.25s2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('hubspotModalTitle') }}
                  </h3>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600
                    dark:hover:bg-neutral-700 dark:hover:text-gray-300"
                  @click="closeHubspotModal"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Modal Content -->
              <div class="flex-1 overflow-y-auto px-6 py-4">
                <!-- Suggested Matches Section -->
                <div class="mb-6">
                  <div class="mb-2 flex items-center gap-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {{ t('suggestedMatches') }}
                    </h4>
                    <span
                      v-if="hubspotCandidates.length > 0"
                      class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30
                        dark:text-orange-300"
                    >
                      {{ hubspotCandidates.length }}
                    </span>
                  </div>
                  <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    {{ t('suggestedMatchesDescription') }}
                  </p>

                  <div
                    v-if="isSearchingCandidates"
                    class="flex items-center justify-center py-6"
                  >
                    <svg
                      class="h-6 w-6 animate-spin text-orange-600"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  </div>

                  <div
                    v-else-if="hubspotCandidates.length === 0"
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-4 text-center dark:border-neutral-600
                      dark:bg-neutral-800"
                  >
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('noSuggestionsFound') }}
                    </p>
                  </div>

                  <div
                    v-else
                    class="space-y-2"
                  >
                    <div
                      v-for="candidate in hubspotCandidates"
                      :key="candidate.uuid"
                      class="group flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 p-3
                        transition-colors hover:border-orange-300 hover:bg-orange-100 dark:border-orange-900/50
                        dark:bg-orange-950/30 dark:hover:border-orange-800 dark:hover:bg-orange-950/50"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-gray-900 dark:text-gray-100">
                            {{ candidate.name || t('unnamed') }}
                          </span>
                          <span
                            v-if="candidate.domain"
                            class="rounded bg-orange-200 px-1.5 py-0.5 text-xs text-orange-800 dark:bg-orange-900/50
                              dark:text-orange-200"
                          >
                            {{ candidate.domain }}
                          </span>
                        </div>
                        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {{ t('matched') }}: {{ candidate.matched_domains.join(', ') }}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="shrink-0 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white transition-colors
                          hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
                          disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="isSelectingHubspotCompany !== null"
                        @click="selectHubspotCompany(candidate)"
                      >
                        <span
                          v-if="isSelectingHubspotCompany === candidate.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-3.5 w-3.5 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCompany') }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Divider -->
                <div class="relative mb-6">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-200 dark:border-neutral-700" />
                  </div>
                  <div class="relative flex justify-center">
                    <span
                      class="bg-white px-3 text-xs text-gray-500 dark:bg-neutral-800 dark:text-gray-400"
                    >
                      {{ t('orSearchAllCompanies') }}
                    </span>
                  </div>
                </div>

                <!-- Search All Companies Section -->
                <div>
                  <h4 class="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('allCompanies') }}
                  </h4>

                  <!-- Search Input -->
                  <div class="relative mb-3">
                    <div
                      class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                    >
                      <svg
                        class="h-4 w-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <input
                      v-model="hubspotSearchQuery"
                      type="text"
                      :placeholder="t('searchPlaceholder')"
                      class="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900
                        placeholder-gray-500 transition-colors focus:border-orange-500 focus:outline-none focus:ring-1
                        focus:ring-orange-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100
                        dark:placeholder-gray-400"
                    />
                    <div
                      v-if="isSearchingAll"
                      class="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      <svg
                        class="h-4 w-4 animate-spin text-orange-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        />
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    </div>
                  </div>

                  <!-- Search Results -->
                  <div
                    v-if="
                      hubspotSearchQuery && !isSearchingAll && hubspotSearchResults.length === 0
                    "
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-4 text-center dark:border-neutral-600
                      dark:bg-neutral-800"
                  >
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('noResultsFound') }}
                    </p>
                  </div>

                  <div
                    v-else-if="hubspotSearchResults.length > 0"
                    class="max-h-60 space-y-2 overflow-y-auto"
                  >
                    <div
                      v-for="company in hubspotSearchResults"
                      :key="company.uuid"
                      class="dark:hover:bg-neutral-750 group flex items-center justify-between rounded-lg border border-gray-200
                        bg-white p-3 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-neutral-700
                        dark:bg-neutral-800 dark:hover:border-neutral-600"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-gray-900 dark:text-gray-100">
                            {{ company.name || t('unnamed') }}
                          </span>
                          <span
                            v-if="company.domain"
                            class="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-neutral-700 dark:text-gray-300"
                          >
                            {{ company.domain }}
                          </span>
                        </div>
                        <a
                          :href="company.hubspot_url"
                          target="_blank"
                          class="mt-1 inline-block text-xs text-gray-500 hover:text-orange-600 hover:underline dark:text-gray-400"
                          @click.stop
                        >
                          {{ company.hubspot_url }}
                        </a>
                      </div>
                      <button
                        type="button"
                        class="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700
                          transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500
                          focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600
                          dark:bg-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-600"
                        :disabled="isSelectingHubspotCompany !== null"
                        @click="selectHubspotCompany(company)"
                      >
                        <span
                          v-if="isSelectingHubspotCompany === company.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-3.5 w-3.5 animate-spin text-orange-600"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCompany') }}</span>
                      </button>
                    </div>
                  </div>

                  <div
                    v-else-if="!hubspotSearchQuery"
                    class="py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {{ t('typeToSearch') }}
                  </div>
                </div>
              </div>

              <!-- Modal Footer -->
              <div
                class="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
                    transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500
                    focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-300
                    dark:hover:bg-neutral-600"
                  @click="closeHubspotModal"
                >
                  {{ t('close') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>

    <!-- Churn Confirmation Dialog -->
    <AlertDialog
      :open="showChurnConfirmDialog"
      @update:open="(open: boolean) => (showChurnConfirmDialog = open)"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('churnConfirmTitle') }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t('churnConfirmDescription') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="cancelChurnConfirm">
            {{ t('cancel') }}
          </AlertDialogCancel>
          <AlertDialogAction
            class="bg-red-600 text-white hover:bg-red-700"
            @click="confirmChurnStatus"
          >
            {{ t('churnConfirmAction') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<style scoped></style>
